// IMPORTANT: Import global less first to avoid CSS cascading issues
import '../less/_app.global.less';

import 'src/lib/polyfills';
import 'src/lib-server/django-shims';
import {QueryClient, QueryClientProvider, Hydrate} from '@tanstack/react-query';
import {useStaticRendering} from 'mobx-react-lite';
import NextApp, {AppContext} from 'next/app';
import {useEffect, useState} from 'react';
import Favicon from 'react-favicon';

import {detectKeyboardNavigation} from '@udemy/design-system-utils';
import {I18nProvider, resolveLocaleDirection, useI18n, UdemyLocale} from '@udemy/i18n';
import {addUDDataToGlobalState, getClientAppContext} from '@udemy/nextjs-core';
import {shimUDPerformance} from '@udemy/performance-rum-client';
import {ShoppingClient} from '@udemy/shopping';
import {StoreProvider} from '@udemy/store-provider';
import {udApi} from '@udemy/ud-api';
import {UDDataProvider, useUDData, useUDLink} from '@udemy/ud-data';

import {i18nPathToBundleMap, i18nPreferred} from 'next.config.i18n';
import {BindI18nGlobals} from 'src/components/bind-i18n-globals/bind-i18n-globals';
import {AppWithLayoutProps} from 'src/components/layout/types';
import {RootStoreProvider} from 'src/contexts/root-store-context';
import {useDevice} from 'src/hooks/useDevice';
import {RootStore} from 'src/stores/root-store';

import {AppContextData, getStaticAppContext} from './api/context.api';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const merge = require('deepmerge'); // https://github.com/TehShrike/deepmerge#include

// Requests are proxied through Next app, so use relative URL for API calls
const DEPLOY_ENV = process.env.DEPLOY_ENV;
if (DEPLOY_ENV === 'local' || DEPLOY_ENV === 'dev') {
    udApi.configure({
        getBaseUrl: () => `${process.env.APP_BASE_PATH}api-2.0`,
    });
}

// TODO: rehyrdate from pageProps instead
const queryClient = new QueryClient();

/**
 * Prevent observable components to re-render on the server. Other wise this causes a memory leak since lots of mobx
 * references being created on the server and never disposed.
 * @see https://github.com/mobxjs/mobx/tree/main/packages/mobx-react-lite#enablestaticrenderingenable-true
 */
// eslint-disable-next-line react-hooks/rules-of-hooks
useStaticRendering(typeof window === 'undefined');

const I18nAccessBootstrapper = () => {
    const i18n = useI18n();
    const udData = useUDData();
    const udLink = useUDLink();

    useEffect(() => {
        if (!udData.isGlobalMeContextLoading) {
            ShoppingClient.initialize({
                i18n,
                udLink,
                isShoppingCartFeatureEnabled: udData.Config.features.shopping_cart,
            });

            if (udData.me.is_authenticated && udData.me.id) {
                ShoppingClient.setUserId(udData.me.id);
            }

            ShoppingClient.fetch();
        }
    }, [i18n, udData, udLink]);

    return null;
};

export default function App({
    Component,
    pageProps,
    footerProps,
    headerProps,
    tipaltiPaymentsHistoryLinkProps,
    instructorHeaderProps,
    locale, // NOTE: this is just the language portion of the locale, i.e. en instead of en_US
    initialUDData,
}: AppWithLayoutProps) {
    const {isMobile, isDesktop, isTablet} = useDevice();
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';
    const [rootStore] = useState(() => {
        const store = new RootStore({
            footerData: footerProps,
            headerData: headerProps,
            tipaltiPaymentsHistoryLink: tipaltiPaymentsHistoryLinkProps,
            instructorHeaderData: instructorHeaderProps,
            locale: fullLocale,
        });
        return store;
    });
    const [udData, setUDData] = useState({
        ...initialUDData,
        isGlobalMeContextLoading: true,
        // Shim device types
        // TODO: deprecate UA based device parsing and prefer responsive design + fresnel
        request: {
            isMobile: isMobile,
            isPC: isDesktop,
            isTablet: isTablet,
            language: locale,
            locale: fullLocale,
        },
    });

    // Shim global scope access to UD data (deprecate this)
    addUDDataToGlobalState(udData);

    // Shim UD.performance global API
    shimUDPerformance();

    useEffect(() => {
        async function bootstrapClient() {
            // Get client UD data
            const {udData: clientUDData} = await getClientAppContext<AppContextData>();

            // Combine server and client app context
            const overrideArrayMerge = (destinationArray: unknown[], sourceArray: unknown[]) =>
                sourceArray;
            const fullUdData = merge(udData, clientUDData, {arrayMerge: overrideArrayMerge});

            // Override a few properties
            const fullUdDataWithOverrides = merge(fullUdData, {
                isGlobalMeContextLoading: false,
                Config: {
                    app_name: process.env.APP_NAME,
                    features: {
                        notice: {
                            smart_bar: false,
                            instructor_bar: true,
                        },
                    },
                },
                userAgnosticTrackingParams: {
                    page_key: pageProps.pageKey,
                },
            });

            // Update udData
            setUDData(fullUdDataWithOverrides);

            // Shim global UD data
            // TODO: deprecate this
            addUDDataToGlobalState(fullUdDataWithOverrides);

            // Initialize the root store
            rootStore.initializeClient(fullUdDataWithOverrides);

            // Turn on keyboard navigation detection
            detectKeyboardNavigation();

            return udData;
        }

        bootstrapClient();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // Use the layout defined at the page level, if available
    // https://nextjs.org/docs/basic-features/layouts#per-page-layouts
    const getLayout = Component.getLayout ?? ((page) => page);

    return (
        <I18nProvider lang={locale} locale={fullLocale} translations={pageProps.translations}>
            <UDDataProvider data={udData}>
                <StoreProvider stores={[]}>
                    <RootStoreProvider rootStore={rootStore}>
                        <QueryClientProvider client={queryClient}>
                            <Hydrate state={pageProps.dehydratedState}>
                                <title>{gettext('Payout Report')}</title>
                                <BindI18nGlobals />
                                <I18nAccessBootstrapper />
                                <Favicon url="/staticx/udemy/images/v8/favicon.ico" />
                                {getLayout(<Component {...pageProps} />)}
                            </Hydrate>
                        </QueryClientProvider>
                    </RootStoreProvider>
                </StoreProvider>
            </UDDataProvider>
        </I18nProvider>
    );
}

App.getInitialProps = async function (appContext: AppContext) {
    /**
     * Required step from https://nextjs.org/docs/advanced-features/custom-app#caveats
     *
     * When you add getInitialProps in your custom app, you must import App from "next/app",
     * call App.getInitialProps(appContext) inside getInitialProps and merge the returned object into the return value.
     */
    const locale = appContext.router.locale as string;
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';
    const [nextAppContext, {header, udData, footer}] = await Promise.all([
        NextApp.getInitialProps(appContext),
        getStaticAppContext({locale}),
    ]);
    return {
        ...nextAppContext,
        footerProps: footer,
        headerProps: header,
        locale,
        initialUDData: udData,
        pageProps: {
            ...nextAppContext.pageProps,
            direction: resolveLocaleDirection(fullLocale as UdemyLocale),
        },
    };
};
